import {
  usePlaybackCurrentTs,
  usePlaybackPlay,
  usePlaybackRange,
} from '@app/core/playback/hooks/usePlayback';
import { WpWordsType } from '@app/modules/search/types';
import { DateTimeStr, GetFragmentDto, Timestamp, toTimestamp } from '@mi-front/core';
import { theme } from '@mi-front/ui';
import { useRef } from 'react';
import styled from 'styled-components';

const SpanStyled = styled.span<{ $isHighlighted?: boolean }>`
  cursor: pointer;
  font-weight: ${(props: any) => (props.$isHighlighted ? 'bold' : 'normal')};
  color: ${(props: any) => (props.$isHighlighted ? theme.aqua : 'black')};
  :hover {
    color: ${theme.aqua};
  }
`;

/**
 * Hook that syncs transcription with the audio player.
 * Allows highlighting words and clicking to play from a position.
 */
export function useFragmentsTranscriptionSync(fragment: GetFragmentDto) {
  const audioTimeRef = useRef<Timestamp>(toTimestamp(0));
  const currentTs = usePlaybackCurrentTs();
  const play = usePlaybackPlay();
  const range = usePlaybackRange<true>();

  /**
   * Renders transcription text with clickable words.
   * Words are highlighted when current time matches.
   */
  const fragmentsTranscriptionPlayerSync = (
    text: WpWordsType[],
    isCurrRow: boolean,
    time: DateTimeStr,
  ) => {
    const chunk = fragment.chunks.find((c) => toTimestamp(c.from) <= toTimestamp(time));

    /**
     * Plays from the selected word's timestamp.
     */
    const playTime = (wordTimestamp: Timestamp) => {
      if (chunk) {
        play({
          startTs: range.from + wordTimestamp * 1000,
          playAfterSeek: true,
        });
      }
      audioTimeRef.current = toTimestamp(time);
    };

    return text.map((item, index) => {
      // Based on playTime function: word timestamps (item.start/item.end) are in seconds relative to range.from
      // currentTs is absolute, so we need to convert it to relative to range.from
      const playbackTimeAbsolute = currentTs ?? 0;

      // Convert word timestamps from seconds to milliseconds (both relative to range.from)
      const wordStart = range.from + item.start * 1000;
      const wordEnd = range.from + item.end * 1000;

      // Compare: both are now in milliseconds relative to range.from
      const isHighlighted = wordStart <= playbackTimeAbsolute && wordEnd >= playbackTimeAbsolute;

      return (
        <SpanStyled
          key={`transcription-${index}`}
          $isHighlighted={isHighlighted && isCurrRow}
          onClick={() => {
            playTime(item.start as Timestamp);
          }}
        >
          {item.word}
        </SpanStyled>
      );
    });
  };

  return {
    fragmentsTranscriptionPlayerSync,
    timeRef: audioTimeRef,
    currentTs,
  };
}
